import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CrossSell, GetPlansResponse } from '../types/appliance.model';
import { CONFIG } from '@domgen/dgx-fe-config';
import { AppConfig } from '@domgen/dgx-components';
import { AccountApiType } from '../types/accountApiType';
import { ApplianceTypeEnum } from '../types/applianceTypeEnum';
import {
  PlanDocumentKey,
  PlanDocumentKeyResponse,
} from '../types/plan-document.model';
import { ClaimType } from '../types/plan-detail.types';
import { BundleResponse } from '../types/bundle.model';
import { AutoRenewalTypeEnum } from '../types/autoRenewalTypeEum';
import { PlanDetailApiResponse } from '../types/plan.model';
import { GetPersonalDetailsResponse } from './get-person-details-response.type';
import { CorrespondenceAddress } from '@domgen/common-person-types';

@Injectable()
export class AppliancesService {
  headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-response-version': '2',
  };

  downloadHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/pdf',
    'x-response-version': '2',
  };

  httpOptions = {
    headers: new HttpHeaders({
      ...this.headers,
    }),
    withCredentials: true,
  };

  imageNotFoundImage = 'image-not-available.png';
  iconNotFoundImage = 'icon-not-available.png';

  cachedUserId: string | undefined;
  cachedPersonDetails: ReplaySubject<CorrespondenceAddress> | undefined =
    undefined;

  constructor(
    private httpClient: HttpClient,
    @Inject(CONFIG) private readonly config: AppConfig
  ) {}

  getPersonCorresponsenceAddress(
    userId: string
  ): Observable<CorrespondenceAddress> {
    if (userId !== this.cachedUserId) this.cachedPersonDetails = undefined;

    if (this.cachedPersonDetails)
      return this.cachedPersonDetails.asObservable();

    return this.httpClient
      .get<GetPersonalDetailsResponse>(
        `${this.config.commonPersonServiceUrl}/get-person-details`,
        this.httpOptions
      )
      .pipe(
        map((response) => {
          return response.result.person.correspondenceAddress;
        }),
        tap((val) => {
          if (!this.cachedPersonDetails) {
            this.cachedPersonDetails = new ReplaySubject(1);
          }
          if (this.cachedPersonDetails) {
            this.cachedPersonDetails.next(val);
          }
        })
      );
  }

  getPlans(): Observable<GetPlansResponse> {
    return this.httpClient
      .post(
        `${this.config.api}${AccountApiType.GetPlans}`,
        {},
        this.httpOptions
      )
      .pipe(map((response: any) => response.result));
  }

  getPlan(planNumber: string): Observable<PlanDetailApiResponse> {
    return this.httpClient
      .post<{ request_id: string; plan: PlanDetailApiResponse }>(
        `${this.config.myAccountApi}${AccountApiType.GetPlan}`,
        {
          ...this.getBody(planNumber),
        },
        this.httpOptions
      )
      .pipe(map((apiResponse: any) => apiResponse.result.plan)); // TODO WL: Is it OK to set apiResponse to any type?
  }

  getPlanDetails(planNumber: string): Observable<PlanDetailApiResponse> {
    return this.httpClient
      .post<{ request_id: string; plan: PlanDetailApiResponse }>(
        `${this.config.myAccountApi}${AccountApiType.GetPlanDetails}`,
        {
          ...this.getBody(planNumber),
        },
        this.httpOptions
      )
      .pipe(map((apiResponse: any) => apiResponse.result.plan)); // TODO WL: Is it OK to set apiResponse to any type?
  }

  isApplianceDisabled(applianceType: string): boolean {
    return (
      applianceType == ApplianceTypeEnum.Cancelled ||
      applianceType == ApplianceTypeEnum.MultiplanCancelled ||
      applianceType == ApplianceTypeEnum.MultiplanExpired ||
      applianceType == ApplianceTypeEnum.Expired
    );
  }

  private getBody(planNumber: string, claimId?: string) {
    return {
      plan_number: planNumber,
      ...(claimId ? { claim_id: claimId } : {}),
    };
  }

  cancelRepair(planNumber: string, openRepairId: string): Observable<any> {
    return this.httpClient
      .post<any>(
        `${this.config.myAccountApi}${AccountApiType.CancelClaim}`,
        {
          planNumber: planNumber,
          claimId: +openRepairId,
        },
        this.httpOptions
      )
      .pipe(map(() => ({})));
  }

  sendPlanEmail(planNumber: string, email: string) {
    return this.httpClient
      .post<any>(
        `${this.config.api}${AccountApiType.ResendEmail}`,
        {
          plan_number: planNumber,
          email: email,
        },
        this.httpOptions
      )
      .pipe(map(() => ({})));
  }

  searchPlanDocumentKey(planNumber: string) {
    return this.httpClient.post<PlanDocumentKeyResponse>(
      `${this.config.myAccountApi}${AccountApiType.SearchDocumentKey}`,
      {
        plan_number: planNumber,
      },
      this.httpOptions
    );
  }

  downloadPlanDocument(
    planNumber: string,
    planDocumentKey: PlanDocumentKey
  ): Observable<Blob> {
    const encodeKey = encodeURIComponent(planDocumentKey.key);
    const encodeHash = encodeURIComponent(planDocumentKey.hash);

    return this.httpClient.request(
      'GET',
      `${this.config.api}${AccountApiType.DownloadPlanDocument}?key=${encodeKey}&hash=${encodeHash}`.replace(
        '{planNumber}',
        planNumber
      ),
      {
        ...this.httpOptions,
        observe: 'body',
        responseType: 'blob',
        headers: new HttpHeaders({
          ...this.downloadHeaders,
        }),
      }
    );
  }

  reportIssue(issue: string) {
    return this.httpClient
      .post<any>(
        `${this.config.api}${AccountApiType.ReportIssue}`,
        {
          issue_description: issue,
        },
        this.httpOptions
      )
      .pipe(map(() => ({})));
  }

  getClaimBundle(
    planNumber: string,
    claimType: ClaimType,
    crossSell: CrossSell | null,
    journey = ''
  ): Observable<BundleResponse> {
    return this.httpClient
      .post<any>(
        `${this.config.myAccountApi}${AccountApiType.ClaimBundle}`,
        {
          plan_number: planNumber,
          claim_type: claimType,
          cross_sell: crossSell,
          journey: journey,
        },
        this.httpOptions
      )
      .pipe(
        map((apiResponse) => ({
          bundle: apiResponse.result.bundle,
          redirectUrl: apiResponse?.result?.redirect_url ?? null,
        }))
      );
  }

  getRebookClaimBundle(
    planNumber: string,
    claimId: string,
    claimType: ClaimType,
    crossSell: CrossSell | null,
    journey = ''
  ): Observable<BundleResponse> {
    return this.httpClient
      .post<any>(
        `${this.config.myAccountApi}${AccountApiType.RebookClaimBundle}`,
        {
          plan_number: planNumber,
          claim_id: claimId,
          claim_type: claimType,
          cross_sell: crossSell,
          journey: journey,
        },
        this.httpOptions
      )
      .pipe(
        map((apiResponse) => ({
          bundle: apiResponse.result.bundle,
          redirectUrl: apiResponse.result.redirect_url,
        }))
      );
  }

  updateAuthRenewal(plan_number: string, auto_renewal: AutoRenewalTypeEnum) {
    return this.httpClient
      .post<any>(
        `${this.config.api}/${AccountApiType.UpdateAutoRenewal}`,
        {
          plan_number: plan_number,
          auto_renewal: auto_renewal,
        },
        this.httpOptions
      )
      .pipe(
        map((response: any) => ({
          response,
        }))
      );
  }
}
